import { FC, MouseEvent, useEffect, useState } from 'react';
import { displayProductInformation } from '../../../helper/cms';
import { CmsProduct, CmsVirtualProduct, ShopContent } from '../../../types/cms_shared_types';
import { Conditional } from '../../Common/Conditional';
import { Button } from '../../Common/Button';
import { Product, ProductListActions } from '../../../types/webshop';

interface ProductCardProps {
  product: CmsProduct;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  openProductConfigurationModal: () => void;
  cmsShopContent: ShopContent;
  isWithinGroup: boolean;
  virtualProduct: CmsVirtualProduct | undefined;
}

export const ProductCard: FC<ProductCardProps> = ({
  product,
  selectedProducts,
  selectedProductsActions,
  openProductConfigurationModal,
  cmsShopContent,
  isWithinGroup,
  virtualProduct,
}) => {
  const [isSelected, setIsSelected] = useState(false);

  const isNonSchablonReportingProduct = product.reporting && !product.isSchablon;
  const isVirtualProduct = virtualProduct !== undefined;

  useEffect(() => {
    if (isVirtualProduct) {
      const anyProductSelected = virtualProduct.products.some((vp) =>
        selectedProducts.some((sp) => sp.code === vp.code),
      );
      setIsSelected(anyProductSelected);
    } else {
      setIsSelected(selectedProducts.some((p) => p.code === product.code));
    }
  }, [product.code, selectedProducts, isVirtualProduct, virtualProduct]);

  const openModal = (event?: MouseEvent) => {
    event?.stopPropagation();
    openProductConfigurationModal();
  };

  const addProduct = () => {
    if (isSelected) {
      return;
    }

    if (isNonSchablonReportingProduct) {
      const newProduct = {
        name: product.name,
        code: product.code,
        quantity: '0',
        timeQuantity: '0',
        musicClass: 'A',
        priceYearInclVat: 0,
        priceYearExclVat: 0,
      };
      selectedProductsActions.add(newProduct);
    } else {
      openModal();
    }
  };

  const removeProduct = () => {
    if (isNonSchablonReportingProduct) {
      const prodIndex = selectedProducts.findIndex((p) => p.code === product.code);
      selectedProductsActions.remove(prodIndex);
    } else {
      openModal();
    }
  };

  const computeBorderStyling = () => {
    return isSelected ? 'border border-sami-green' : ' border-sami-card-border ';
  };

  const className = isWithinGroup
    ? 'w-full sm:max-w-[600px] sm:w-[300px] lg:w-[500px] xl:w-[600px] flex flex-col mb-2 pl-6'
    : 'w-full sm:max-w-[600px] sm:w-[300px] lg:w-[500px] xl:w-[600px] flex flex-col mb-4 pl-6 ' +
      `py-6 rounded-lg border ${computeBorderStyling()} ${isSelected ? '' : 'cursor-pointer'} shadow hover:shadow-md`;

  return (
    <div onClick={addProduct} className={className}>
      <div className="flex flex-row items-center">
        <div className="flex flex-col w-4/6 pr-2 sm:w-4/5 sm:pr-4">
          <div style={{ hyphens: 'auto' }} className="text-base font-bold text-text-dark">
            {isVirtualProduct ? virtualProduct?.name : product.name}
          </div>
          {isSelected && isNonSchablonReportingProduct && (
            <div className="mt-2 text-sm text-text-dark">{product.description}</div>
          )}
        </div>
        <div className="pr-4 mx-auto text-sami-green">
          <Conditional
            condition={isSelected}
            trueRender={
              <Button
                filled
                text={
                  isNonSchablonReportingProduct
                    ? cmsShopContent.productChoiceStep.removeProductButton
                    : cmsShopContent.productChoiceStep.editProductButton
                }
                onClick={removeProduct}
              />
            }
            falseRender={<Button text={cmsShopContent.productChoiceStep.addProductButton} onClick={addProduct} hover />}
          />
        </div>
      </div>

      <Conditional
        condition={isSelected}
        trueRender={
          <>
            {product.reporting || product.isSchablon ? null : (
              <div className="mr-1 text-sm font-medium text-grey">
                {cmsShopContent.productChoiceStep.selectionHeader}
              </div>
            )}
            {!isWithinGroup && (
              <div className="mt-1 mr-1 text-sm text-grey">
                {product.reporting || product.isSchablon
                  ? cmsShopContent.productChoiceStep.reportingProductInformation
                  : displayProductInformation(product, selectedProducts)}
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
